import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leaveTypeService } from '../services/leaveTypeService';
import { LeaveType, CreateLeaveTypeData, UpdateLeaveTypeData } from '../types/leaveType';
import './LeaveTypesPage.css';

const LeaveTypesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null);
  const [formData, setFormData] = useState<CreateLeaveTypeData>({
    name: '',
    is_paid: false,
    requires_balance: false,
    display_order: 0,
    description: '',
  });

  useEffect(() => {
    loadLeaveTypes();
  }, []);

  const loadLeaveTypes = async () => {
    try {
      setLoading(true);
      const data = await leaveTypeService.getAll();
      setLeaveTypes(data);
      setError(null);
    } catch (err: any) {
      console.error('休暇種別の取得に失敗しました:', err);
      setError(err.response?.data?.error || '休暇種別の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (leaveType?: LeaveType) => {
    if (leaveType) {
      setEditingLeaveType(leaveType);
      setFormData({
        name: leaveType.name,
        is_paid: leaveType.is_paid,
        requires_balance: leaveType.requires_balance,
        display_order: leaveType.display_order,
        description: leaveType.description || '',
      });
    } else {
      setEditingLeaveType(null);
      setFormData({
        name: '',
        is_paid: false,
        requires_balance: false,
        display_order: leaveTypes.length + 1,
        description: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLeaveType(null);
    setFormData({
      name: '',
      is_paid: false,
      requires_balance: false,
      display_order: 0,
      description: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingLeaveType) {
        await leaveTypeService.update(editingLeaveType.id, formData as UpdateLeaveTypeData);
        alert('休暇種別を更新しました');
      } else {
        await leaveTypeService.create(formData);
        alert('休暇種別を作成しました');
      }
      handleCloseModal();
      loadLeaveTypes();
    } catch (err: any) {
      console.error('休暇種別の保存に失敗しました:', err);
      alert(err.response?.data?.error || '休暇種別の保存に失敗しました');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('この休暇種別を削除しますか？\n※残数が残っている場合は削除できません。')) {
      return;
    }

    try {
      await leaveTypeService.delete(id);
      alert('休暇種別を削除しました');
      loadLeaveTypes();
    } catch (err: any) {
      console.error('休暇種別の削除に失敗しました:', err);
      alert(err.response?.data?.error || '休暇種別の削除に失敗しました');
    }
  };

  if (loading) {
    return <div className="leave-types-page"><p>読み込み中...</p></div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>休暇種別管理</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleOpenModal()}
              style={{
                padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              + 新規作成
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              ダッシュボードに戻る
            </button>
          </div>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="leave-types-table-container">
        <table className="leave-types-table">
          <thead>
            <tr>
              <th>表示順</th>
              <th>休暇種別名</th>
              <th>有給/無給</th>
              <th>残数管理</th>
              <th>説明</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {leaveTypes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">休暇種別が登録されていません</td>
              </tr>
            ) : (
              leaveTypes.map((leaveType) => (
                <tr key={leaveType.id}>
                  <td>{leaveType.display_order}</td>
                  <td>{leaveType.name}</td>
                  <td>
                    <span className={leaveType.is_paid ? 'badge badge-paid' : 'badge badge-unpaid'}>
                      {leaveType.is_paid ? '有給' : '無給'}
                    </span>
                  </td>
                  <td>
                    <span className={leaveType.requires_balance ? 'badge badge-balance' : 'badge badge-no-balance'}>
                      {leaveType.requires_balance ? 'あり' : 'なし'}
                    </span>
                  </td>
                  <td>{leaveType.description || '-'}</td>
                  <td>
                    {leaveType.system_code ? (
                      /*
                       * 法定休暇（年次有給休暇）は編集・削除させない。
                       * 改名や残数管理の解除で、年次有給休暇管理簿と
                       * 年5日の取得義務の集計がまとめて壊れるため。
                       * サーバー側でも 403 で拒否しているが、
                       * 押せてしまうと「なぜ弾かれるのか」が伝わらないので
                       * ボタン自体を出さず、理由を添える。
                       */
                      <span
                        title="年次有給休暇は名称や設定を変更できません"
                        style={{ color: '#64748B', fontSize: '13px', whiteSpace: 'nowrap' }}
                      >
                        法定休暇のため変更不可
                      </span>
                    ) : (
                      <>
                        <button
                          className="btn-edit"
                          onClick={() => handleOpenModal(leaveType)}
                        >
                          編集
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(leaveType.id)}
                        >
                          削除
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingLeaveType ? '休暇種別編集' : '休暇種別作成'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">休暇種別名 *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="例: 有給休暇"
                />
              </div>

              <div className="form-group">
                <label htmlFor="display_order">表示順</label>
                <input
                  type="number"
                  id="display_order"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_paid}
                    onChange={(e) => setFormData({ ...formData, is_paid: e.target.checked })}
                  />
                  有給休暇として扱う
                </label>
                <p className="form-help">有給の場合、休暇時間が労働時間にカウントされます</p>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.requires_balance}
                    onChange={(e) => setFormData({ ...formData, requires_balance: e.target.checked })}
                  />
                  残数管理を行う
                </label>
                <p className="form-help">残数管理を行う場合、付与された残数がないと使用できません</p>
              </div>

              <div className="form-group">
                <label htmlFor="description">説明</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="例: 年次有給休暇"
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  キャンセル
                </button>
                <button type="submit" className="btn-primary">
                  {editingLeaveType ? '更新' : '作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypesPage;
